/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button, { KINDS } from '@medflex/components/stories/Button';
import { useTranslation } from 'react-i18next';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useFormContext, useFieldArray } from 'react-hook-form';
import TimeInputField from './TimeInputField';

const EditWeekday = ({ weekdayIndex }) => {
  const { t } = useTranslation('physician', {
    keyPrefix: 'InquiryManagementModule.PhoneOpeningHours.form',
  });

  const { control, watch, formState, errors, setValue } = useFormContext();
  const open = watch(`weekdays[${weekdayIndex}].active`);
  const {
    fields: slots,
    insert,
    remove,
  } = useFieldArray({
    control,
    name: `weekdays[${weekdayIndex}].slots`,
  });

  useEffect(() => {
    if (slots.length === 0 && open) {
      insert(0, { startTime: '', endTime: '' });
    }
  }, [insert, slots.length, open, setValue, weekdayIndex]);

  const getErrorMessage = useCallback(
    index => {
      const startTimeError = errors.weekdays?.[weekdayIndex]?.slots[index]?.startTime;
      const endTimeError = errors.weekdays?.[weekdayIndex]?.slots[index]?.endTime;
      if (startTimeError) {
        return startTimeError.message;
      }
      if (endTimeError) {
        return endTimeError.message;
      }
      return '';
    },
    [errors, weekdayIndex],
  );

  if (!open) {
    return <p className="pt-2">{t('closed')}</p>;
  }

  return (
    <div className="flex flex-col items-start space-y-4">
      {slots?.map((slot, index) => (
        <fieldset className="flex-col w-full space-y-2" key={slot.id}>
          <div className="flex flex-row space-x-3 sm:space-x-4 items-center">
            <TimeInputField
              className="flex-initial min-w-16 sm:min-w-20"
              defaultValue={slot.startTime}
              placeholder={t('time_placeholder')}
              name={`weekdays[${weekdayIndex}].slots[${index}].startTime`}
              id={`weekdays[${weekdayIndex}].slots[${index}].startTime`}
            />
            <span>{t('times_delimiter')}</span>
            <TimeInputField
              className="flex-initial min-w-16 sm:min-w-20"
              defaultValue={slot.endTime}
              placeholder={t('time_placeholder')}
              name={`weekdays[${weekdayIndex}].slots[${index}].endTime`}
              id={`weekdays[${weekdayIndex}].slots[${index}].endTime`}
            />
            <Button
              shape="circle"
              size="xs"
              kind={KINDS.SECONDARY_GHOST}
              onClick={() => insert(index + 1, { startTime: '', endTime: '' })}
            >
              <PlusCircleIcon className="h-6 w-6" />
            </Button>
            <Button
              shape="circle"
              size="xs"
              kind={KINDS.SECONDARY_GHOST}
              className={`${index === 0 ? 'hidden' : ''}`}
              onClick={() => remove(index)}
            >
              <TrashIcon className="h-6 w-6" />
            </Button>
          </div>
          {!formState.isValid && getErrorMessage(index) && (
            <p className="text-xs text-red-600">{getErrorMessage(index)}</p>
          )}
        </fieldset>
      ))}
    </div>
  );
};

EditWeekday.propTypes = {
  weekdayIndex: PropTypes.number.isRequired,
};

EditWeekday.defaultProps = {};

export default EditWeekday;
